import PropTypes from 'prop-types';
import { ResponseFooterType } from '../../types/ResponseFooterType';
import { getFooterTranslations, useNavigationTranslationsQuery } from '../../services/NavigationItemServices';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import LanguageIcon from '../../assets/images/LanguageIconWhite.svg';
import LinkIcon from '../../assets/images/icon/DocLinkIconWhite.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { updatePageLang } from '../../helpers/cxDataLayer';
import { sanitizeCmsHtml } from '../../helpers/sanitizeCmsHtml';

const LANGUAGE_OPTIONS = ["English:en_HK", "繁體中文:zh_HK", "简体中文:sc_CN"];

const hasCmsValue = (value: string, key: string) => value.trim() !== '' && value !== key;

function ResponsiveFooter() {
  const { t, i18n } = useTranslation('footer');
  const location = useLocation();
  const navigate = useNavigate();

  const navigationQuery = useNavigationTranslationsQuery();
  const footerTranslations = navigationQuery.data ? getFooterTranslations(navigationQuery.data) : undefined;

  const hasSocialLinks = !!footerTranslations && footerTranslations.contact_sns.length > 0;
  const contactUsText = t('contact_us_text', { defaultValue: 'Contact us' });
  const contactUsLink = t('contact_us_link');
  const termsAndConditionsText = t('terms_and_conditions_text', { defaultValue: 'Terms and conditions' });
  const termsAndConditionsLink = t('terms_and_conditions_link');
  const cookiePolicyText = t('cookie_policy_text', { defaultValue: 'Cookie policy' });
  const cookiePolicyLink = t('cookie_policy_link');
  const contactText = t('contact_text', { defaultValue: 'Follow us' });
  const copyright = t('copyright', { defaultValue: '' });

  const renderSocialIcons = () => (footerTranslations?.contact_sns ?? []).map((item, index) => {
    const socialKey = item.platform_key || item.link || `footer-social-${index}`;

    return (
      <SocialIcon
        key={socialKey}
        link={item.link}
        logo={item.logo}
        icon_alt_text={item.icon_alt_text}
        platform_key={item.platform_key}
      />
    );
  });

  const onClick = (lang: string) => {
    const currentPage = location.pathname.slice(7);
    void i18n.changeLanguage(lang);
    navigate(`/${lang}/${currentPage}`);
    updatePageLang({
      language: lang.split('_')[0],
      country: lang.split('_')[1],
      locale: lang
    });
  };



  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__first-row">
          <div className="footer__text">
            <div className="footer__link-items">
              <a className='link' href={hasCmsValue(contactUsLink, 'contact_us_link') ? contactUsLink : undefined}>
                {contactUsText}
                <img src={LinkIcon} className='link' alt='link icon'/>
              </a>
            </div>
            <div className="footer__link-items">
              <a className='link' href={hasCmsValue(termsAndConditionsLink, 'terms_and_conditions_link') ? termsAndConditionsLink : undefined} target='_blank' rel="noreferrer">
                {termsAndConditionsText}
                <img src={LinkIcon} className='link' alt='link icon'/>
              </a>
            </div>
            <div className="footer__link-items">
              <a className='link' href={hasCmsValue(cookiePolicyLink, 'cookie_policy_link') ? cookiePolicyLink : undefined} target='_blank' rel="noreferrer">
                {cookiePolicyText}
                <img src={LinkIcon} className='link' alt='link icon'/>
              </a>
            </div>
          </div>
          <hr className="footer__divider" />
          {hasSocialLinks ? (
            <div className="footer__socials footer__socials--mobile" aria-label={contactText}>
              {renderSocialIcons()}
            </div>
          ) : null}
          {hasSocialLinks ? <hr className="footer__divider footer__divider--mobile-only" /> : null}
          <div className='footer__lang'>
            <img className='footer__lang__icon' src={LanguageIcon} alt="Language Icon"></img>
            {LANGUAGE_OPTIONS.map((value) => {
              const [label, lang] = value.split(":");
              if (i18n.language !== lang) {
                return (
                  <button key={lang} onClick={() => onClick(lang)}>
                    <p>{label}</p>
                  </button>
                );
              }
            })}
          </div>
        </div>
        {hasSocialLinks ? (
          <div className="footer__socials footer__socials--desktop" aria-label={contactText}>
            {renderSocialIcons()}
          </div>
        ) : null}
        <hr className="footer__divider" />
        <div className='footer__bottom'>
          <div className="footer__bottom__copyright">
            {hasCmsValue(copyright, 'copyright') ? parse(sanitizeCmsHtml(copyright)) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default ResponsiveFooter;

SocialIcon.propTypes = {
  link: PropTypes.string,
  logo: PropTypes.string,
  icon_alt_text: PropTypes.string,
  platform_key: PropTypes.string,
};

function SocialIcon({ link, logo, icon_alt_text }: ResponseFooterType) {
  return (
    <a
      className="footer__social-icon"
      href={link}
      rel="noreferrer noopener"
      target="_blank"
    >
      <span className="visually-hidden">{icon_alt_text}</span>
      <img alt="" src={logo} aria-hidden="true" />
    </a>
  );
}